import { useMutation } from '@tanstack/react-query';
import { DatePicker, Form, Space, TableColumnsType } from 'antd';
import { AnyObject } from 'antd/es/_util/type';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { ExpensesApis } from 'apis/ExpensesApis';
import { IGetRoutes, IUpdateExpense } from 'apis/GraphQL/Variables/expenses';
import { PlantApi } from 'apis/PlantApis';
import { EditGrayIcon, EditIcon, EyeIcon, User3Icon } from 'assets/svg';
import { ButtonAction, InputForm, SelectForm, Switch } from 'components';
import InputNumberForm from 'components/Input/InputNumberForn';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import usePagination from 'hooks/usePagination';
import { ITable } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { IRouteCost } from 'pages/Accountant/RouteCost/RouteCostPage';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import formatNumber, {
  formatCurrency,
  toastError,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';

const { SaveTwoTone, CloseCircleTwoTone } = icons;

export interface ITableRouteCostProps {
  defaultFilter: any;
  data: ITable<IRouteCost>;
  loading: boolean;
  refetch: (isExport?: boolean, headerRow?: TableColumnsType) => void;
}

const TableRouteCost = (
  { defaultFilter, loading, data, refetch }: ITableRouteCostProps,
  ref: any,
) => {
  const { user } = useUser();
  const [form] = useForm();
  const { page, page_size: pageSize } = usePagination();
  const [editIndex, setEditIndex] = useState(-1);
  const [routes, setRoutes] = useState<Array<DefaultOptionType>>([]);
  const [plants, setPlants] = useState({
    items: [],
    pageInfo: {},
  });
  const [expenseTypes, setExpenseTypes] = useState<DefaultOptionType[]>([]);

  const { mutate: getRoutes, isPending: getRoutesLoading } = useMutation({
    mutationFn: (params: IGetRoutes) => ExpensesApis.getRoutes(params),
    onSuccess: (data) => {
      const result = data.data?.getSalesPlanningList?.items || [];

      setRoutes(
        result.map((route: any) => ({
          label: route.routeName,
          value: route.routeId,
        })),
      );
    },
  });

  const { mutate: getAllPlant } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants({ items: [], pageInfo: {} });
      setPlants(() => ({
        items: items.map((item: any) => ({
          label: item.id,
          value: item.id,
        })),
        pageInfo: data.data.getPlant.pageInfo,
      }));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getAllExpensesType, isPending: getAllExpensesTypeLoading } =
    useMutation({
      mutationFn: () => ExpensesApis.getAllExpensesType(),
      onSuccess: (data) => {
        const types = data.data?.getExpensesType?.items ?? [];

        setExpenseTypes(
          types.map((type: any) => ({
            label: type.name,
            value: type.id,
            expensesAmount: type.expensesAmount,
          })),
        );
      },
    });

  const { mutate: updateExpenses, isPending: updateExpensesLoading } =
    useMutation({
      mutationFn: (params: IUpdateExpense) =>
        ExpensesApis.updateExpenses(params),
      onSuccess: () => {
        setEditIndex(-1);
        refetch();
      },
    });

  const handleClickEdit = useCallback(
    (index: number) => {
      setEditIndex(index);

      const expenses = data.items[index];

      getRoutes({
        implementationDateFrom: dayjs(expenses.startDate).format(
          'YYYY-MM-DD 00:00:00',
        ),
        plantId: expenses.tteamSales?.plantId,
        skip: 0,
        take: 100,
        teamId: defaultFilter.teamSalesSubId,
      });

      form.setFieldsValue({
        startDate: dayjs(expenses.startDate),
        routeId: expenses.troute?.id,
        routeName: expenses.troute?.name,
        expensesTypeId: expenses.texpensesType?.id,
        expensesTypeName: expenses.texpensesType?.name,
        expensesAmount: expenses.texpensesType?.expensesAmount,
        actualExpenses: expenses.actualExpenses,
        numberPerson: expenses.numberPerson,
        finalExpenses: expenses.finalExpenses,
        note: expenses.note || undefined,
        plantId: expenses.tteamSales?.plantId || undefined,
      });
    },
    [data.items, defaultFilter.teamSalesSubId, form, getRoutes],
  );

  const handleCancelEdit = useCallback(() => {
    setEditIndex(-1);
  }, []);

  const handleChangeRoute = useCallback(
    (value: any) => {
      const route: any = routes.find((item: any) => item.value === value);

      form.setFieldsValue({
        routeName: route?.label,
      });
    },
    [form, routes],
  );

  const handleChangeType = useCallback(
    (value: any) => {
      const type = expenseTypes.find((item) => item.value === value);

      form.setFieldsValue({
        expensesAmount: type?.expensesAmount,
        expensesTypeName: type?.label,
      });
    },
    [expenseTypes, form],
  );

  const handleChangeFinalExpenses = useCallback(() => {
    const values = form.getFieldsValue();
    const actualExpenses = values.actualExpenses;
    const numberPerson = values.numberPerson;

    if (!actualExpenses || !numberPerson)
      return form.setFieldsValue({ finalExpenses: undefined });

    form.setFieldsValue({
      finalExpenses: actualExpenses * numberPerson,
    });
  }, [form]);

  const validate = useCallback(async () => {
    const startDate = form.getFieldValue('startDate');

    if (!startDate) {
      toastError('Vui lòng chọn ngày đi');
      return false;
    }

    const plantId = form.getFieldValue('plantId');

    if (!plantId) {
      toastError('Vui lòng chọn chi nhánh');
      return false;
    }

    const routeId = form.getFieldValue('routeId');

    if (!routeId) {
      toastError('Vui lòng chọn tuyến');
      return false;
    }

    const expensesTypeId = form.getFieldValue('expensesTypeId');

    if (!expensesTypeId) {
      toastError('Vui lòng chọn loại chi phí');
      return false;
    }

    const actualExpenses = form.getFieldValue('actualExpenses');

    if (!actualExpenses) {
      toastError('Vui lòng nhập chi phí thực tế');
      return false;
    }

    const numberPerson = form.getFieldValue('numberPerson');

    if (!numberPerson) {
      toastError('Vui lòng nhập số lượng');
      return false;
    }

    return true;
  }, [form]);

  const handleSave = useCallback(async () => {
    const isValid = await validate();

    if (!isValid) return;

    const values = form.getFieldsValue();
    const expenses = data.items[editIndex];

    console.log('values', values);
    updateExpenses({
      startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
      routeId: values.routeId,
      numberPerson: values.numberPerson,
      note: values.note,
      finalExpenses: values.finalExpenses,
      expensesType: values.expensesTypeId,
      actualExpenses: values.actualExpenses,
      customerId: expenses.tcustomer?.id,
      id: expenses.id,
      policyExpenses: values.expensesAmount,
      teamSalesId: expenses.tteamSales.id,
      userId: expenses.tuser?.employeeId || user?.id || '',
      plantId: values.plantId,
    });
  }, [data.items, editIndex, form, updateExpenses, user?.id, validate]);

  const handleChangeDate = useCallback(
    (value: any) => {
      const date = dayjs(value).format('YYYY-MM-DD 00:00:00');

      form.setFieldsValue({
        routeId: undefined,
        routeName: undefined,
      });

      getRoutes({
        implementationDateFrom: date,
        plantId: form.getFieldValue('plantId'),
        skip: 0,
        take: 100,
        teamId: defaultFilter.teamSalesSubId,
      });
    },
    [defaultFilter.teamSalesSubId, form, getRoutes],
  );

  const columns: TableColumnsType = useMemo(
    () => [
      {
        width: 50,
        title: 'STT',
        align: 'center',
        render: (_: any, __: any, index: number) =>
          index + 1 + pageSize * (page - 1),
      },
      {
        type: 'date',
        formatDate: 'DD/MM/YYYY',
        width: 130,
        title: 'Ngày đi',
        dataIndex: 'startDate',
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name="startDate"
                className="m-0"
                initialValue={dayjs(value)}
              >
                <DatePicker
                  placeholder="Chọn ngày"
                  format="DD/MM/YYYY"
                  maxDate={dayjs()}
                  onChange={handleChangeDate}
                />
              </Form.Item>
            );

          return value ? dayjs(value).format('DD/MM/YYYY') : '-';
        },
      },
      {
        width: 110,
        title: 'Mã nhân viên',
        dataIndex: ['tuser', 'employeeId'],
      },
      {
        width: 200,
        title: 'Tên nhân viên',
        dataIndex: ['tuser', 'fullName'],
      },
      {
        width: 130,
        title: 'Mã tuyến',
        dataIndex: ['troute', 'id'],
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item name={'routeId'} className="m-0" initialValue={value}>
                <SelectForm
                  options={routes}
                  loading={getRoutesLoading}
                  placeholder="Chọn tuyến"
                  size="middle"
                  onChange={handleChangeRoute}
                />
              </Form.Item>
            );

          return value || '-';
        },
      },
      {
        width: 150,
        title: 'Tên tuyến',
        dataIndex: ['troute', 'name'],
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'routeName'}
                className="m-0"
                initialValue={value}
              >
                <InputForm size="middle" placeholder="Tên tuyến" readOnly />
              </Form.Item>
            );

          return value || '-';
        },
      },
      {
        width: 90,
        title: 'Mã loại chi',
        dataIndex: 'expensesType',
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'expensesTypeId'}
                className="m-0"
                initialValue={value}
              >
                <SelectForm
                  size="middle"
                  allowClear={false}
                  options={expenseTypes}
                  fieldNames={{
                    label: 'value',
                  }}
                  placeholder="Chọn loại chi"
                  onChange={handleChangeType}
                  loading={getAllExpensesTypeLoading}
                />
              </Form.Item>
            );

          return value || '-';
        },
      },
      {
        width: 150,
        title: 'Loại chi',
        dataIndex: ['texpensesType', 'name'],
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'expensesTypeName'}
                className="m-0"
                initialValue={value}
              >
                <InputForm size="middle" readOnly placeholder="Loại chi" />
              </Form.Item>
            );

          return value || '-';
        },
      },
      {
        type: 'number',
        currency: 'vi-VN',
        width: 150,
        title: 'Chính sách công ty',
        align: 'right',
        dataIndex: ['texpensesType', 'expensesAmount'],
        render: (value: number, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'expensesAmount'}
                className="m-0"
                initialValue={value}
              >
                <InputNumberForm
                  placeholder="Chính sách công ty"
                  readOnly
                  size="middle"
                  formatter={(value: any) => {
                    return value ? formatNumber(value) : '';
                  }}
                  parser={(value: any) => {
                    return parseFloat(
                      value.replace(/\./g, '')?.replace(',', '.'),
                    );
                  }}
                />
              </Form.Item>
            );

          return formatCurrency({
            value,
          });
        },
      },
      {
        type: 'number',
        currency: 'vi-VN',
        width: 150,
        title: 'Chi phí thực tế',
        dataIndex: 'actualExpenses',
        align: 'right',
        render: (value: number, record: AnyObject, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'actualExpenses'}
                className="m-0"
                initialValue={value}
              >
                <InputNumberForm
                  placeholder="Chi phí thực tế"
                  size="middle"
                  formatter={(value: any) => {
                    return value ? formatNumber(value) : '';
                  }}
                  parser={(value: any) => {
                    return parseFloat(
                      value.replace(/\./g, '')?.replace(',', '.'),
                    );
                  }}
                  onChange={handleChangeFinalExpenses}
                />
              </Form.Item>
            );

          if (value > record.texpensesType.expensesAmount) {
            return (
              <span style={{ color: 'red', fontWeight: 700 }}>
                {formatCurrency({ value })}
              </span>
            );
          }

          return formatCurrency({ value });
        },
      },
      {
        width: 80,
        title: 'Số lượng',
        dataIndex: 'numberPerson',
        align: 'right',
        render: (value: number, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'numberPerson'}
                className="m-0"
                initialValue={value}
              >
                <InputNumberForm
                  placeholder="Số lượng"
                  size="middle"
                  min={1}
                  formatter={(value: any) =>
                    value ? value.replace(/[^0-9]/g, '') : ''
                  } // Loại bỏ ký tự không phải số
                  parser={(value) =>
                    value ? parseInt(value.replace(/[^0-9]/g, ''), 10) : 0
                  }
                  onChange={handleChangeFinalExpenses}
                />
              </Form.Item>
            );

          return value;
        },
      },
      {
        type: 'number',
        currency: 'vi-VN',
        width: 150,
        title: 'Thành tiền',
        dataIndex: 'finalExpenses',
        align: 'right',
        render: (value: number, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'finalExpenses'}
                className="m-0"
                initialValue={value}
              >
                <InputNumberForm
                  placeholder="Thành tiền"
                  readOnly
                  size="middle"
                  formatter={(value: any) => {
                    return value ? formatNumber(value) : '';
                  }}
                  parser={(value: any) => {
                    return parseFloat(
                      value.replace(/\./g, '')?.replace(',', '.'),
                    );
                  }}
                />
              </Form.Item>
            );

          return formatCurrency({ value });
        },
      },
      {
        width: 150,
        title: 'Ghi chú',
        dataIndex: 'note',
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item name={'note'} className="m-0" initialValue={value}>
                <InputForm size="middle" placeholder="Ghi chú" />
              </Form.Item>
            );

          return value || '-';
        },
      },
      {
        width: 100,
        title: 'Đơn vị',
        dataIndex: ['texpensesType', 'tunit', 'name'],
      },
      {
        width: 150,
        title: 'Bộ phận',
        dataIndex: ['tuser', 'jobTitle'],
      },
      {
        width: 150,
        title: 'Mã đội',
        dataIndex: ['tteamSales', 'subId'],
      },
      {
        width: 150,
        title: 'Tên đội',
        dataIndex: ['tteamSales', 'name'],
      },
      {
        width: 150,
        title: 'Chi nhánh',
        dataIndex: ['tteamSales', 'plantId'],
        render: (value: string, _: any, index: number) => {
          if (editIndex === index)
            return (
              <Form.Item
                name={'plantId'}
                initialValue={value}
                rules={[{ required: true, message: 'Chọn chi nhánh' }]}
                className="m-0"
              >
                <SelectForm
                  size="middle"
                  allowClear={false}
                  options={plants?.items || []}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  placeholder="Chọn chi nhánh"
                  showSearch
                />
              </Form.Item>
            );

          return value || '-';
        },
      },
      {
        width: 180,
        title: 'Người tạo',
        dataIndex: ['tcreateUser', 'fullName'],
        className: 'align-center',
      },
      {
        width: 200,
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        type: 'date',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        width: 180,
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
      },
      {
        width: 200,
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        type: 'date',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        title: 'Thao tác',
        fixed: 'right',
        width: 170,
        align: 'center',
        render: (_: any, record: any, index: number) => {
          const isDisableEdit = editIndex !== -1 || !user?.isSale;

          return editIndex == index ? (
            <Space size={'middle'} className="text-lg">
              <ButtonAction tooltip="Lưu" onClick={handleSave}>
                <SaveTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
              <ButtonAction tooltip="Hủy" onClick={handleCancelEdit}>
                <CloseCircleTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
            </Space>
          ) : (
            <Space size={'middle'} className="text-lg ">
              <ButtonAction tooltip="Xem chi tiết">
                <EyeIcon />
              </ButtonAction>
              <ButtonAction tooltip="Khách hàng">
                <User3Icon />
              </ButtonAction>
              <ButtonAction
                tooltip="Chỉnh sửa"
                onClick={() => handleClickEdit(index)}
                disabled={isDisableEdit}
              >
                {!isDisableEdit ? <EditIcon /> : <EditGrayIcon />}
              </ButtonAction>
              <Switch />
            </Space>
          );
        },
      },
    ],
    [
      editIndex,
      expenseTypes,
      getAllExpensesTypeLoading,
      getRoutesLoading,
      handleCancelEdit,
      handleChangeDate,
      handleChangeFinalExpenses,
      handleChangeRoute,
      handleChangeType,
      handleClickEdit,
      handleSave,
      page,
      pageSize,
      plants?.items,
      routes,
      user?.isAdmin,
      user?.isHO,
      user?.isSale,
    ],
  );

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    refetch(true, columns);
  };

  useEffect(() => {
    getAllPlant();
    getAllExpensesType();
  }, [getAllExpensesType, getAllPlant]);

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={data.items}
          rowKey={(record) => record?.id}
          loading={loading || updateExpensesLoading}
          pagination={{
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
};

export default forwardRef(TableRouteCost);
